import { type ISurveySection } from '../../models/SurveyTemplate'
import { type ISection } from './components/SurveyFeatureStepper/types'
import { buildAnswerFieldName, getModdedSectionId, MULTIPLE_CHOICE_SEPERATOR } from './components/SurveyFeatureStepper/utils'

/**
 * Updates the local state draft with the latest response items.
 * The itemId acts as the key for the value in the draft.
 */
export const updateLocalDraft = (itemId: string, value: string | string[], draftKey: string, retainClearedFields = false): void => {
  const currDraft = JSON.parse(localStorage.getItem(draftKey) ?? '{}')

  // if value is blank, remove the item from the draft
  const isValueEmpty = value === '' || (Array.isArray(value) && value.length === 0)
  let updatedDraft
  if (isValueEmpty && !retainClearedFields) {
    // Fresh surveys drop a cleared field from the draft (original behavior).
    const { [itemId]: _, ...rest } = currDraft
    updatedDraft = rest
  } else {
    // CE-193: on a reopened (previously submitted) survey, keep an explicit empty marker
    // for a cleared field so the deliberate clear is remembered on refresh/return and wins
    // over the previously submitted API answer during hydration (instead of resurrecting it).
    updatedDraft = { ...currDraft, [itemId]: value }
  }

  // if updatedDraft is empty, remove the draft from localStorage
  if (Object.keys(updatedDraft).length === 0) {
    localStorage.removeItem(draftKey)
  } else {
    localStorage.setItem(draftKey, JSON.stringify(updatedDraft))
  }
}

/**
 * Updates the responses for the sections based on the local draft.
 */
export const getResponsesFromDraft = (sections: ISection[], draftKey: string): ISection[] => {
  const localDraft = JSON.parse(localStorage.getItem(draftKey) ?? '{}')

  return sections.map(section => {
    const updatedItems = section.surveyItems.map(item => {
      // CR-213: section.surveySectionId is already the modded id on transformed sections.
      const name = buildAnswerFieldName(item.itemId, section.surveySectionId)
      const savedValue = localDraft[name]

      // CE-193: an in-progress local draft always wins so Save-and-Exit work is not
      // discarded (including a deliberately cleared empty string). When there is no local
      // draft entry, fall back to the value already seeded from the submitted answer
      // (getItemsWithBranches) instead of clearing it.
      let valueStr: string
      if (savedValue === undefined) {
        valueStr = item.value
      } else if (Array.isArray(savedValue)) {
        valueStr = savedValue.join(MULTIPLE_CHOICE_SEPERATOR)
      } else if (typeof savedValue === 'string') {
        valueStr = savedValue
      } else {
        // Defensive: a legacy or hand-edited draft could hold a non-string primitive
        // (e.g. null or a number). Coerce it so downstream string ops (toLowerCase/split)
        // never throw.
        valueStr = savedValue == null ? '' : String(savedValue)
      }
      return { ...item, value: valueStr }
    })
    return { ...section, surveyItems: updatedItems, children: getResponsesFromDraft(section.children, draftKey) }
  })
}

/**
 * CE-193: builds react-hook-form default values from previously submitted answers
 * returned on the survey GET. Used when reopening a submitted survey with no local
 * draft so the form fields display prior responses.
 *
 * Field names follow the shared `buildAnswerFieldName` convention (see RenderSections),
 * which keys by itemId + modded section id so repeat-per-instructor and CR-213
 * cross-section duplicate answers stay distinct. These sections are raw ISurveySection,
 * so the modded id is built here from instructorId + surveySectionId. Multiselect answers
 * are preserved as arrays to match the form control's expected shape.
 */
export const buildSurveyResponseDefaults = (sections: ISurveySection[] | undefined): Record<string, string | string[]> => {
  const defaults: Record<string, string | string[]> = {}

  sections?.forEach(section => {
    section.surveyItems?.forEach(item => {
      const answer = item.answer
      const name = buildAnswerFieldName(item.itemId, getModdedSectionId(section.instructorId, section.surveySectionId))

      // Narrow instead of casting: only hydrate a field that has a non-empty answer.
      if (typeof answer === 'string' && answer !== '') {
        defaults[name] = answer
      } else if (Array.isArray(answer) && answer.length > 0) {
        defaults[name] = answer
      }
    })
  })

  return defaults
}
